import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { EmbedProvider } from "@/contexts/EmbedContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";

// Eagerly loaded: homepage (critical path)
import Home from "./pages/Home";

// Lazy-loaded public pages
const ExperienceDetail = lazy(() => import("./pages/ExperienceDetail"));
const ExperienceRedirect = lazy(() => import("./pages/ExperienceRedirect"));
const BookingManage = lazy(() => import("./pages/BookingManage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ProviderLanding = lazy(() => import("./pages/ProviderLanding"));
const ProviderLandingLocalized = lazy(() => import("./pages/ProviderLandingLocalized"));
const PartnerRegister = lazy(() => import("./pages/PartnerRegister"));
const PartnerVerify = lazy(() => import("./pages/PartnerVerify"));
const PartnerResendVerify = lazy(() => import("./pages/PartnerResendVerify"));
const PartnerPhoneVerify = lazy(() => import("./pages/PartnerPhoneVerify"));
const DestinationGuide = lazy(() => import("./pages/DestinationGuide"));
const DestinationsDirectory = lazy(() => import("./pages/DestinationsDirectory"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Vision = lazy(() => import("./pages/Vision"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Review = lazy(() => import("./pages/Review"));
const Checkout = lazy(() => import("./pages/Checkout"));
const GuestLogin = lazy(() => import("./pages/GuestLogin"));
const GuestRegister = lazy(() => import("./pages/GuestRegister"));
const GuestResetPassword = lazy(() => import("./pages/GuestResetPassword"));
const GuideRegister = lazy(() => import("./pages/GuideRegister"));
const CategoryGuard = lazy(() => import("./pages/CategoryGuard"));
const GuestRecommend = lazy(() => import("./pages/GuestRecommend"));
const ThingsToDoCity = lazy(() => import("./pages/ThingsToDoCity"));
const ProviderProfile = lazy(() => import("./pages/ProviderProfile"));
const CategoryCity = lazy(() => import("./pages/CategoryCity"));
const IntentPage = lazy(() => import("./pages/IntentPage"));
const LocalizedRouter = lazy(() => import("./components/LocalizedRouter"));

// Lazy-loaded account pages
const AccountOrders = lazy(() => import("./pages/account/Orders"));
const AccountFavorites = lazy(() => import("./pages/account/Favorites"));
const AccountProfile = lazy(() => import("./pages/account/Profile"));
const AccountMessages = lazy(() => import("./pages/account/Messages"));

// Lazy-loaded portal pages
const Login = lazy(() => import("./pages/portal/Login"));
const Dashboard = lazy(() => import("./pages/portal/Dashboard"));
const Requests = lazy(() => import("./pages/portal/Requests"));
const RequestDetailPage = lazy(() => import("./pages/portal/RequestDetail"));
const PortalExperiences = lazy(() => import("./pages/portal/Experiences"));
const ExperienceCreate = lazy(() => import("./pages/portal/ExperienceCreate"));
const ExperienceEdit = lazy(() => import("./pages/portal/ExperienceEdit"));
const PortalExperiencePreview = lazy(() => import("./pages/portal/ExperiencePreview"));
const PortalAnalytics = lazy(() => import("./pages/portal/Analytics"));
const OnboardingWizard = lazy(() => import("./pages/portal/OnboardingWizard"));
const PortalLayout = lazy(() => import("./components/portal/PortalLayout"));
const ProtectedRoute = lazy(() => import("./components/portal/ProtectedRoute"));
const ResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const Recommend = lazy(() => import("./pages/portal/Recommend"));
const PortalReferrals = lazy(() => import("./pages/portal/Referrals"));
const PortalCoupons = lazy(() => import("./pages/portal/Coupons"));
const PortalPricingRules = lazy(() => import("./pages/portal/PricingRules"));
const PortalNotifications = lazy(() => import("./pages/portal/Notifications"));
const PortalPhone = lazy(() => import("./pages/portal/Phone"));
const PortalGuides = lazy(() => import("./pages/portal/Guides"));

// Lazy-loaded admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminProviders = lazy(() => import("./pages/admin/Providers"));
const AdminProviderDetail = lazy(() => import("./pages/admin/ProviderDetail"));
const AdminExperiences = lazy(() => import("./pages/admin/Experiences"));
const AdminExperienceDetail = lazy(() => import("./pages/admin/ExperienceDetail"));
const AdminMarkupRules = lazy(() => import("./pages/admin/MarkupRules"));
const AdminExperiencePreview = lazy(() => import("./pages/admin/ExperiencePreview"));
const AdminBookings = lazy(() => import("./pages/admin/Bookings"));
const AdminBookingDetail = lazy(() => import("./pages/admin/BookingDetail"));
const AdminOnboardingReview = lazy(() => import("./pages/admin/OnboardingReview"));
const AdminOnboardingReviewDetail = lazy(() => import("./pages/admin/OnboardingReviewDetail"));
const AdminQuizManagement = lazy(() => import("./pages/admin/QuizManagement"));
const AdminReviews = lazy(() => import("./pages/admin/Reviews"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));
const AdminRoleAuditLog = lazy(() => import("./pages/admin/RoleAuditLog"));
const AdminPayouts = lazy(() => import("./pages/admin/Payouts"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminLeadDetail = lazy(() => import("./pages/admin/LeadDetail"));
const AdminGuests = lazy(() => import("./pages/admin/Guests"));
const AdminReferrals = lazy(() => import("./pages/admin/Referrals"));
const AdminCoupons = lazy(() => import("./pages/admin/Coupons"));
const AdminPricingRules = lazy(() => import("./pages/admin/PricingRules"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminOrderDetail = lazy(() => import("./pages/admin/OrderDetail"));
const AdminDemandDashboard = lazy(() => import("./pages/admin/DemandDashboard"));
const AdminDemandConfig = lazy(() => import("./pages/admin/DemandConfig"));
const AdminIntegrations = lazy(() => import("./pages/admin/Integrations"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminAnnouncements = lazy(() => import("./pages/admin/Announcements"));
const AdminNotificationHistory = lazy(() => import("./pages/admin/NotificationHistory"));
const AdminTranslations = lazy(() => import("./pages/admin/Translations"));
const AdminSequences = lazy(() => import("./pages/admin/Sequences"));
const AdminTags = lazy(() => import("./pages/admin/Tags"));
const AdminProspects = lazy(() => import("./pages/admin/Prospects"));
const AdminSegments = lazy(() => import("./pages/admin/Segments"));
const AdminIcpConfig = lazy(() => import("./pages/admin/IcpConfig"));

// Lazy-loaded affiliate pages
const AffiliateRoute = lazy(() => import("./components/affiliate/AffiliateRoute"));
const AffiliateLayout = lazy(() => import("./components/affiliate/AffiliateLayout"));
const AffiliateLanding = lazy(() => import("./pages/affiliates/Landing"));
const AffiliateProviderRecruiters = lazy(() => import("./pages/affiliates/ProviderRecruiters"));
const AffiliateApply = lazy(() => import("./pages/affiliates/Apply"));
const AffiliateLogin = lazy(() => import("./pages/affiliates/Login"));
const AffiliateTerms = lazy(() => import("./pages/affiliates/Terms"));
const AffiliatePrivacy = lazy(() => import("./pages/affiliates/Privacy"));
const AffiliateFAQ = lazy(() => import("./pages/affiliates/FAQ"));
const AffiliateDashboard = lazy(() => import("./pages/affiliates/Dashboard"));
const AffiliateInvite = lazy(() => import("./pages/affiliates/Invite"));
const AffiliateProviders = lazy(() => import("./pages/affiliates/Providers"));
const AffiliateEarnings = lazy(() => import("./pages/affiliates/Earnings"));
const AffiliateNotifications = lazy(() => import("./pages/affiliates/Notifications"));
const AffiliateSettings = lazy(() => import("./pages/affiliates/Settings"));
const AffiliateOnboarding = lazy(() => import("./pages/affiliates/Onboarding"));
const AffiliateAnalytics = lazy(() => import("./pages/affiliates/Analytics"));
const AffiliateCreators = lazy(() => import("./pages/affiliates/Creators"));
const AffiliateContentLinks = lazy(() => import("./pages/affiliates/ContentLinks"));
const AffiliateContentPerformance = lazy(() => import("./pages/affiliates/ContentPerformance"));
const AffiliateAdminLayout = lazy(() => import("./components/admin/AffiliateAdminLayout"));
const AffiliatesDashboard = lazy(() => import("./pages/admin/AffiliatesDashboard"));
const AffiliateApplications = lazy(() => import("./pages/admin/AffiliateApplications"));
const AffiliateRecruiters = lazy(() => import("./pages/admin/AffiliateRecruiters"));
const AffiliateDetail = lazy(() => import("./pages/admin/AffiliateDetail"));
const AffiliateCommissions = lazy(() => import("./pages/admin/AffiliateCommissions"));
const AffiliatePayoutsAdmin = lazy(() => import("./pages/admin/AffiliatePayouts"));
const AffiliateSettingsAdmin = lazy(() => import("./pages/admin/AffiliateSettings"));
const AffiliateAudit = lazy(() => import("./pages/admin/AffiliateAudit"));
const AffiliateFinance = lazy(() => import("./pages/admin/AffiliateFinance"));
const AffiliateInvoices = lazy(() => import("./pages/affiliates/Invoices"));
const AffiliateFraud = lazy(() => import("./pages/admin/AffiliateFraud"));
const AffiliateAICopilot = lazy(() => import("./pages/affiliates/AICopilot"));
const AffiliateIntegrations = lazy(() => import("./pages/admin/AffiliateIntegrations"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <EmbedProvider>
          <LanguageProvider>
          <CurrencyProvider>
          <CartProvider>
          <AuthProvider>
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/experiences" element={<Navigate to="/" replace />} />
            <Route path="/experiences/:citySlug/:slug" element={<ExperienceDetail />} />
            <Route path="/experiences/:slug" element={<ExperienceRedirect />} />
            <Route path="/partner" element={<ProviderLanding />} />
            <Route path="/partner/register" element={<PartnerRegister />} />
            <Route path="/partner/verify" element={<PartnerVerify />} />
            <Route path="/partner/resend-verify" element={<PartnerResendVerify />} />
            <Route path="/partner/verify-phone" element={<PartnerPhoneVerify />} />
            <Route path="/provider" element={<Navigate to="/partner" replace />} />
            <Route path="/se/provider" element={<Navigate to="/se/partner" replace />} />
            <Route path="/se/partner" element={<ProviderLandingLocalized lang="sv" />} />
            <Route path="/booking/:manageToken" element={<BookingManage />} />
            <Route path="/review/:token" element={<Review />} />
            <Route path="/recommend/:token" element={<GuestRecommend />} />
            <Route path="/destinations" element={<DestinationsDirectory />} />
            <Route path="/destinations/:countrySlug/:citySlug" element={<DestinationGuide />} />
            <Route path="/destinations/:slug" element={<DestinationGuide />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/vision" element={<Vision />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/login" element={<GuestLogin />} />
            <Route path="/register" element={<GuestRegister />} />
            <Route path="/reset-password" element={<GuestResetPassword />} />
            <Route path="/account" element={<AccountOrders />} />
            <Route path="/account/favorites" element={<AccountFavorites />} />
            <Route path="/account/profile" element={<AccountProfile />} />
            <Route path="/account/messages" element={<AccountMessages />} />
            <Route path="/guide/register" element={<GuideRegister />} />

            {/* Affiliate public pages */}
            <Route path="/affiliates" element={<AffiliateLanding />} />
            <Route path="/affiliates/provider-recruiters" element={<AffiliateProviderRecruiters />} />
            <Route path="/affiliates/creators" element={<AffiliateCreators />} />
            <Route path="/affiliates/apply" element={<AffiliateApply />} />
            <Route path="/affiliates/login" element={<AffiliateLogin />} />
            <Route path="/affiliates/terms" element={<AffiliateTerms />} />
            <Route path="/affiliates/privacy" element={<AffiliatePrivacy />} />
            <Route path="/affiliates/faq" element={<AffiliateFAQ />} />

            {/* Affiliate portal routes */}
            <Route path="/affiliates/onboarding" element={<AffiliateOnboarding />} />
            <Route
              path="/affiliates"
              element={
                <AffiliateRoute>
                  <AffiliateLayout />
                </AffiliateRoute>
              }
            >
              <Route path="dashboard" element={<AffiliateDashboard />} />
              <Route path="invite" element={<AffiliateInvite />} />
              <Route path="providers" element={<AffiliateProviders />} />
              <Route path="content-links" element={<AffiliateContentLinks />} />
              <Route path="content-performance" element={<AffiliateContentPerformance />} />
              <Route path="earnings" element={<AffiliateEarnings />} />
              <Route path="analytics" element={<AffiliateAnalytics />} />
              <Route path="notifications" element={<AffiliateNotifications />} />
              <Route path="settings" element={<AffiliateSettings />} />
              <Route path="invoices" element={<AffiliateInvoices />} />
              <Route path="ai-copilot" element={<AffiliateAICopilot />} />
            </Route>

            {/* Portal routes */}
            <Route path="/portal/login" element={<Login />} />
            <Route path="/portal/reset-password" element={<ResetPassword />} />
            <Route
              path="/portal/onboarding"
              element={
                <ProtectedRoute skipOnboardingCheck>
                  <OnboardingWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal"
              element={
                <ProtectedRoute>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="requests" element={<Requests />} />
              <Route path="requests/:id" element={<RequestDetailPage />} />
              <Route path="experiences" element={<PortalExperiences />} />
              <Route path="experiences/create" element={<ExperienceCreate />} />
              <Route path="experiences/:id" element={<ExperienceEdit />} />
              <Route path="experiences/:id/preview" element={<PortalExperiencePreview />} />
              <Route path="guides" element={<PortalGuides />} />
              <Route path="analytics" element={<PortalAnalytics />} />
              <Route path="recommend" element={<Recommend />} />
              <Route path="referrals" element={<PortalReferrals />} />
              <Route path="coupons" element={<PortalCoupons />} />
              <Route path="pricing" element={<PortalPricingRules />} />
              <Route path="notifications" element={<PortalNotifications />} />
              <Route path="phone" element={<PortalPhone />} />
            </Route>

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="providers" element={<AdminProviders />} />
              <Route path="providers/:id" element={<AdminProviderDetail />} />
              <Route path="onboarding" element={<AdminOnboardingReview />} />
              <Route path="onboarding/:id" element={<AdminOnboardingReviewDetail />} />
              <Route path="quiz" element={<AdminQuizManagement />} />
              <Route path="experiences" element={<AdminExperiences />} />
              <Route path="experiences/:id" element={<AdminExperienceDetail />} />
              <Route path="experiences/:id/preview" element={<AdminExperiencePreview />} />
              <Route path="markup-rules" element={<AdminMarkupRules />} />
              <Route path="pricing-rules" element={<AdminPricingRules />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="bookings/:id" element={<AdminBookingDetail />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="leads/:id" element={<AdminLeadDetail />} />
              <Route path="guests" element={<AdminGuests />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="role-audit" element={<AdminRoleAuditLog />} />
              <Route path="payouts" element={<AdminPayouts />} />
              <Route path="referrals" element={<AdminReferrals />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="demand" element={<AdminDemandDashboard />} />
              <Route path="demand-config" element={<AdminDemandConfig />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="notifications" element={<AdminNotificationHistory />} />
              <Route path="translations" element={<AdminTranslations />} />
              <Route path="sequences" element={<AdminSequences />} />
              <Route path="tags" element={<AdminTags />} />
              <Route path="prospects" element={<AdminProspects />} />
              <Route path="segments" element={<AdminSegments />} />
              <Route path="icp-config" element={<AdminIcpConfig />} />
              <Route path="affiliates" element={<AffiliateAdminLayout />}>
                <Route index element={<AffiliatesDashboard />} />
                <Route path="applications" element={<AffiliateApplications />} />
                <Route path="recruiters" element={<AffiliateRecruiters />} />
                <Route path=":id" element={<AffiliateDetail />} />
                <Route path="commissions" element={<AffiliateCommissions />} />
                <Route path="payouts" element={<AffiliatePayoutsAdmin />} />
                <Route path="settings" element={<AffiliateSettingsAdmin />} />
                <Route path="audit" element={<AffiliateAudit />} />
                <Route path="finance" element={<AffiliateFinance />} />
                <Route path="fraud" element={<AffiliateFraud />} />
                <Route path="integrations" element={<AffiliateIntegrations />} />
              </Route>
            </Route>

            {/* SEO programmatic pages */}
            <Route path="/things-to-do/:citySlug" element={<ThingsToDoCity />} />
            <Route path="/providers/:providerSlug" element={<ProviderProfile />} />
            <Route path="/:categorySlug/:citySlug" element={<CategoryCity />} />
            <Route path="/:categorySlug" element={<CategoryGuard />} />

            {/* Localized URL catch-all: /:lang/* resolves to canonical routes */}
            <Route path="/:lang/*" element={<LocalizedRouter />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </AuthProvider>
          </CartProvider>
          </CurrencyProvider>
          </LanguageProvider>
        </EmbedProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
  </HelmetProvider>
);

export default App;
